import { 
  Box, 
  Grid, 
  CircularProgress, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton, 
  Tooltip,
  useTheme
} from "@mui/material";
import { 
  ViewModule as ViewModuleIcon, 
  ViewList as ViewListIcon,
  Image as ImageIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Link as LinkIcon,
  List as ListIcon
} from "@mui/icons-material";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { useRef, useState, useEffect, useCallback, memo } from "react";
import { ApiEndpoint, FlexibleDataItem, RelatedEntityItem } from "../../types";
import { getSafeThumbnailUrl } from "../../utilities/mediaUtils";
import { getDataItemName, getDataItemDescription } from "../../utilities/dataSourceAdapters";
import { isFulcrumPhotoUrl } from "../../utilities/fulcrumLibService";
import SearchBar from "../common/SearchBar";
import DataItemCard from "./DataItemCard";
import AddNewCard from "./AddNewCard";
import { FulcrumImage } from "../common/FulcrumImage";
import { CMButton } from "../common/CMButton";
import { useConfig } from "../../contexts/ConfigContext";
import { getEntityColor } from "../../utilities/dataItemUtils";
import { UI_CONSTANTS } from "../../constants/uiConstants";
import { useSnackbar } from "notistack";
import EndpointFilter from "../common/EndpointFilter";

// TODO: Enable search functionality for related items
// When this is set to true, search will work in related items mode
// Currently disabled because search filtering is not implemented for related items
const ENABLE_SEARCH_IN_RELATED_ITEMS = false;

// Enhanced FlexibleDataItem for related items with endpoint information
type EnhancedRelatedItem = FlexibleDataItem & {
  properties?: {
    relatedEndpoint?: ApiEndpoint;
    relatedCategory?: string;
    endpointColor?: string;
    [key: string]: any;
  };
};

interface DataGridProps {
  data: FlexibleDataItem[];
  onSelect: (item: FlexibleDataItem) => void;
  compact?: boolean;
  currentEndpoint?: ApiEndpoint | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  isLoadingMore?: boolean;
  onServerSearch?: (searchTerm: string) => Promise<void>;
  serverSearchLoading?: boolean;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  onAdd?: () => void;
  showAddNew?: boolean;
  onFilteredCountChange?: (filteredCount: number, hiddenDueToImages: number) => void;
  viewMode?: 'cards' | 'table';
  onViewModeChange?: (mode: 'cards' | 'table') => void;
  // Related items mode
  showRelatedItems?: boolean;
  relatedItemsData?: RelatedEntityItem[];
  onToggleRelatedItems?: () => void;
  selectedEntityName?: string;
  // Status text display
  onStatusText?: () => string;
}

const DataGrid = memo(({
  data = [],
  onSelect,
  compact = false,
  currentEndpoint,
  searchTerm,
  setSearchTerm,
  onLoadMore,
  hasMoreData = false,
  isLoadingMore = false,
  onServerSearch,
  serverSearchLoading = false,
  onRefresh,
  refreshDisabled = false,
  onAdd,
  showAddNew = true,
  onFilteredCountChange,
  viewMode = 'cards',
  onViewModeChange,
  showRelatedItems = false,
  relatedItemsData = [],
  onToggleRelatedItems,
  selectedEntityName,
  onStatusText,
}: DataGridProps) => {
  const config = useConfig();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  
  // Related items filtering state - organize by endpoint/category (workshop branch approach)
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  
  // Reset filters when switching away from related items mode
  React.useEffect(() => {
    if (!showRelatedItems) {
      setSelectedCategoryFilter('all');
    }
  }, [showRelatedItems]);
  
  // Glass morphism styling utility
  const glassStyles = {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : 'rgba(255, 255, 255, 0.2)'}`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
      : '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
  };

  // Local filtering of data based on searchTerm using generalized getter functions
  const filteredData = React.useMemo(() => {
    let processedData = data;
    let hiddenDueToImages = 0;
    
    // First filter: hide cards without images if config setting is enabled
    if (config.hideCardsWithoutImages) {
      const originalCount = processedData.length;
      processedData = processedData.filter((item: any) => {
        const imageUrl = getSafeThumbnailUrl(item, currentEndpoint);
        return imageUrl && imageUrl.trim() !== '';
      });
      hiddenDueToImages = originalCount - processedData.length;
    }
    
    // Second filter: search term filtering
    if (searchTerm.trim()) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      processedData = processedData.filter((item: any) => {
        const itemName = getDataItemName(item, currentEndpoint || undefined);
        const itemDescription = getDataItemDescription(item, currentEndpoint || undefined);
        return itemName?.toLowerCase().includes(lowerSearchTerm) ||
               itemDescription?.toLowerCase().includes(lowerSearchTerm);
      });
    }
    
    // Return both the filtered data and counts as an object
    return {
      data: processedData,
      filteredCount: processedData.length,
      hiddenDueToImages
    };
  }, [data, searchTerm, currentEndpoint, config.hideCardsWithoutImages]);

  // Use useEffect to notify parent about count changes (prevents render-phase state updates)
  React.useEffect(() => {
    onFilteredCountChange?.(filteredData.filteredCount, filteredData.hiddenDueToImages);
  }, [filteredData.filteredCount, filteredData.hiddenDueToImages, onFilteredCountChange]);

  // Create special "Add New" item and combine with filtered items
  const ADD_NEW_ITEM_ID = '__ADD_NEW__';
  const addNewItem: any = {
    id: ADD_NEW_ITEM_ID,
    name: 'Add New',
    description: '',
    isAddNewItem: true
  };

  // Only show "Add New" card when:
  // 1. showAddNew is true
  // 2. onAdd callback is provided
  // 3. No search term is active (so user sees it when browsing normally)
  // 4. currentEndpoint exists (we know what we're adding)
  // 5. If geometry is required, it must be Point type (we can only create Point geometries)
  // 6. Media creation is blocked (use dedicated upload flows instead)
  const canCreateGeometry = !currentEndpoint?.requiresGeometry || currentEndpoint?.geometryType === "Point";
  const isMediaEndpoint = currentEndpoint?.entityType === "media" || currentEndpoint?.tableName === "media";
  const shouldShowAddNew = showAddNew && onAdd && !searchTerm.trim() && currentEndpoint && canCreateGeometry && !isMediaEndpoint;
  
  // Convert related items to FlexibleDataItem format with enhanced type detection and filtering
  const convertedRelatedItems: EnhancedRelatedItem[] = React.useMemo(() => {
    if (!showRelatedItems || !relatedItemsData.length) {
      console.log('[DataGrid] convertedRelatedItems: not processing - showRelatedItems:', showRelatedItems, 'relatedItemsData.length:', relatedItemsData.length);
      return [];
    }
    
    console.log('[DataGrid] Converting related items to display format:', {
      showRelatedItems,
      relatedItemsCount: relatedItemsData.length,
      sampleItem: relatedItemsData[0] ? {
        id: relatedItemsData[0].id,
        entity_name: relatedItemsData[0].entity_name,
        category: (relatedItemsData[0] as any).category,
        relationshipType: (relatedItemsData[0] as any).relationshipType
      } : null
    });
    
    // Parse relationship description to determine direct vs indirect (from RelatedItemsRing logic)
    const parseRelDescription = (desc: string | undefined): { relationship_type?: 'direct' | 'indirect' | 'default_to_direct' } => {
      if (!desc) return { relationship_type: 'default_to_direct' };
      try {
        const parsed = JSON.parse(desc);
        if (parsed.relationship_type === 'indirect') {
          return { relationship_type: 'indirect' };
        }
        return { relationship_type: 'default_to_direct' };
      } catch (e) {
        return { relationship_type: 'default_to_direct' };
      }
    };
    
    // Map each related item to include endpoint information and enhanced type data
    const itemsWithEndpoints = relatedItemsData.map(item => {
      const category = (item as any).category || 'unknown';
      const relationshipType = (item as any).relationshipType || 'unknown';
      
      // Determine entity type based on relationship structure (from RelatedItemsRing logic)
      let entityType: 'geo' | 'related' | 'media' = 'related';
      if (relationshipType === 'media') {
        entityType = 'media';
      } else if (relationshipType === 'geo_entities') {
        entityType = 'geo';
      } else if (relationshipType === 'related_entities') {
        entityType = 'related';
      }
      
      // Parse relationship description for direct/indirect classification
      const parsedDesc = parseRelDescription(item.rel_description);
      const isDirect = parsedDesc.relationship_type !== 'indirect';
      
      // Find matching endpoint by category or entity type
      const matchingEndpoint = config.apiEndpoints.find(ep => 
        ep.returnedRelationshipsKey === category ||
        ep.tableName === category ||
        ep.name.toLowerCase() === category.toLowerCase()
      );
      
      // Debug endpoint matching for related items
      if (process.env.NODE_ENV === 'development') {
        console.log('[DataGrid] Related item endpoint matching:', {
          itemId: item.id,
          itemName: item.entity_name,
          category,
          itemKeys: Object.keys(item),
          itemProperties: item,
          matchingEndpointName: matchingEndpoint?.name,
          matchingEndpointTableName: matchingEndpoint?.tableName,
          allEndpoints: config.apiEndpoints.map(ep => ({
            name: ep.name,
            tableName: ep.tableName,
            returnedRelationshipsKey: ep.returnedRelationshipsKey
          }))
        });
      }
      
      return {
        id: item.id,
        // CRITICAL: Use item.name (not entity_name) based on actual item structure
        name: (item as any).name || item.entity_name || 'Unknown',
        description: item.entity_description || '',
        thumbnail: item.entity_thumbnail || (item as any).thumbnail || undefined,
        // CRITICAL: Use the matching endpoint's tableName as entityType for correct API routing
        entityType: matchingEndpoint?.tableName || (item as any).entityType || 'related',
        geometry: '',
        properties: {
          ...item,
          rel_description: item.rel_description,
          is_primary: item.is_primary,
          // Add endpoint information for sorting and coloring
          relatedEndpoint: matchingEndpoint,
          relatedCategory: category,
          endpointColor: matchingEndpoint ? getEntityColor(matchingEndpoint.tableName as any) : getEntityColor(entityType),
          // CRITICAL: Store the correct endpoint for this related item
          correctEndpoint: matchingEndpoint,
          // Enhanced type information from RelatedItemsRing logic
          relationshipType: relationshipType,
          entityTypeClassification: entityType, // 'geo' | 'related' | 'media'
          isDirect: isDirect,
          isIndirect: !isDirect,
          // Type-specific styling
          typeColor: entityType === 'media' ? '#ff6b6b' : 
                    entityType === 'geo' ? '#4ecdc4' : 
                    '#45b7d1', // Default for 'related'
          typeBadge: entityType === 'media' ? '📷' : 
                    entityType === 'geo' ? '📍' : 
                    '🔗', // Default for 'related'
          directBadge: isDirect ? '🔗' : '🔄' // Direct vs indirect indicator
        }
      };
    });
    
    // Apply category and directness filtering (workshop branch approach)
    let filteredItems = itemsWithEndpoints;
    
    // Filter by category/endpoint
    if (selectedCategoryFilter !== 'all') {
      filteredItems = filteredItems.filter(item => 
        item.properties?.relatedCategory === selectedCategoryFilter
      );
    }
    
    // Note: Directness filtering removed - not using indirect relationships anymore
    
    // Sort by endpoint/category first, then by item name (workshop branch approach)
    return filteredItems.sort((a, b) => {
      // Primary sort: endpoint name (this groups items by their data source)
      const endpointA = a.properties?.relatedEndpoint?.name || 'Unknown';
      const endpointB = b.properties?.relatedEndpoint?.name || 'Unknown';
      if (endpointA !== endpointB) {
        return endpointA.localeCompare(endpointB);
      }
      
      // Secondary sort: item name
      return a.name.localeCompare(b.name);
    });
  }, [relatedItemsData, showRelatedItems, config.apiEndpoints, selectedCategoryFilter]);

  // Use related items data when in related items mode, otherwise use main data
  const baseDisplayData: FlexibleDataItem[] = showRelatedItems ? convertedRelatedItems : filteredData.data;
  
  // Get ALL available categories for filtering (not filtered by current selection)
  const allAvailableCategories = React.useMemo(() => {
    console.log('🔍 [ENDPOINT-FILTER-DEBUG] allAvailableCategories calculation:', {
      showRelatedItems,
      relatedItemsDataLength: relatedItemsData.length,
      willReturnEmpty: !showRelatedItems || !relatedItemsData.length,
      timestamp: new Date().toISOString()
    });
    
    if (!showRelatedItems || !relatedItemsData.length) {
      console.log('🔍 [ENDPOINT-FILTER-DEBUG] allAvailableCategories returning empty array');
      return [];
    }
    
    // Use original relatedItemsData to get ALL categories, not filtered convertedRelatedItems
    const rawCategories = relatedItemsData.map(item => (item as any).category);
    console.log('🔍 [ENDPOINT-FILTER-DEBUG] Raw categories from original data:', rawCategories);
    
    const categories = Array.from(new Set(
      rawCategories.filter((category): category is string => Boolean(category)) // Type guard to ensure string[]
    )).sort();
    
    console.log('🔍 [ENDPOINT-FILTER-DEBUG] Final ALL available categories:', {
      categories,
      categoriesLength: categories.length,
      timestamp: new Date().toISOString()
    });
    return categories;
  }, [relatedItemsData, showRelatedItems]); // Use relatedItemsData, not convertedRelatedItems

  // Group items by endpoint for row-based display
  const groupedByEndpoint = React.useMemo(() => {
    if (!showRelatedItems || !convertedRelatedItems.length) return {};
    
    const groups: Record<string, FlexibleDataItem[]> = {};
    
    convertedRelatedItems.forEach(item => {
      const category = item.properties?.relatedCategory || 'unknown';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    
    console.log('[DataGrid] Grouped items by endpoint:', {
      groupCount: Object.keys(groups).length,
      groups: Object.entries(groups).map(([category, items]) => ({
        category,
        count: items.length,
        endpointName: config.apiEndpoints.find(ep => 
          ep.returnedRelationshipsKey === category ||
          ep.tableName === category
        )?.name || category
      }))
    });
    
    return groups;
  }, [convertedRelatedItems, showRelatedItems, config.apiEndpoints]);
  
  
  const filteredItems = (shouldShowAddNew && !showRelatedItems) 
    ? [addNewItem, ...baseDisplayData]
    : baseDisplayData;

  // Configurable table columns - simplified for better UX
  const [showAllColumns, setShowAllColumns] = useState(false);
  
  // Dynamic column generation for table view
  const generateTableColumns = useCallback(() => {
    if (showAllColumns) {
      // Show all available columns when expanded
      if (filteredItems.length === 0) return [];
      
      const allKeys = new Set<string>();
      filteredItems.forEach(item => {
        Object.keys(item).forEach(key => {
          if (key !== 'isAddNewItem' && key !== 'geometry') {
            allKeys.add(key);
          }
        });
      });
      
      // Prioritize important columns
      const priorityColumns = ['name', 'description', 'id', 'created_at', 'updated_at'];
      const remainingColumns = Array.from(allKeys).filter(key => !priorityColumns.includes(key));
      
      return [...priorityColumns.filter(col => allKeys.has(col)), ...remainingColumns.slice(0, 5)];
    } else {
      // Simple view: just name and description
      return ['name', 'description'];
    }
  }, [filteredItems, showAllColumns]);

  const tableColumns = generateTableColumns();

  const parentRef = useRef<HTMLDivElement>(null);
  const [itemsPerRow, setItemsPerRow] = useState(6);
  
  // Force recalculation of itemsPerRow when switching back to cards view
  const recalculateItemsPerRow = useCallback(() => {
    if (parentRef.current) {
      const containerWidth = parentRef.current.getBoundingClientRect().width;
      const minItemWidth = compact ? UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_COMPACT : UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_FULL;
      const newItemsPerRow = Math.floor(containerWidth / minItemWidth);
      setItemsPerRow(Math.max(1, newItemsPerRow));
      console.log(`[DataGrid] Recalculated itemsPerRow: ${newItemsPerRow} (containerWidth: ${containerWidth})`);
    }
  }, [compact]);
  
  // Trigger recalculation when switching to cards view
  useEffect(() => {
    if (viewMode === 'cards') {
      // Small delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        recalculateItemsPerRow();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [viewMode, recalculateItemsPerRow]);
  
  // Memoize the onSelect handler
  const handleSelect = useCallback((item: FlexibleDataItem) => {
    console.log('🚨 [FULCRUM-DEBUG] DataGrid item selected:', {
      id: item.id, 
      idType: typeof item.id,
      name: item.name,
      currentEndpoint: {
        name: currentEndpoint?.name,
        tableName: currentEndpoint?.tableName,
        formId: currentEndpoint?.formId,
        dataSourceType: currentEndpoint?.dataSourceType
      },
      itemKeys: Object.keys(item),
      itemDataSourceType: (item as any).dataSourceType,
      itemFormId: (item as any).form_id,
      itemFulcrumData: (item as any).fulcrum_data ? {
        hasFormId: !!(item as any).fulcrum_data.form_id,
        formId: (item as any).fulcrum_data.form_id
      } : null,
      hasCoordinates: !!(
        ((item as any).longitude && (item as any).latitude) || 
        ((item as any).geometry?.coordinates) || 
        ((item as any)._geometry?.coordinates)
      ),
      geometry: (item as any).geometry,
      coordinates: (item as any).coordinates,
      timestamp: new Date().toISOString(),
      note: 'This data will be passed to SelectionContext'
    });
    
    // Check if this is a Fulcrum photo that shouldn't be selectable
    // Enhanced detection: check for Fulcrum photo characteristics regardless of entityType
    const itemData = { ...item, ...(item as any).properties };
    
    const isFulcrumPhoto = !!(
      // Direct Fulcrum photo indicators
      (itemData as any).fulcrum_photo?.photo_id || 
      (itemData as any).fulcrum_data?.form_id || 
      (itemData as any)._source?.type === 'fulcrum' ||
      // Media type indicators combined with Fulcrum data source
      ((itemData as any).media_type === 'photo' && (
        (itemData as any).dataSourceType === 'fulcrum' ||
        (itemData as any).form_id ||
        (itemData as any).fulcrum_id
      )) ||
      // Photo name pattern from Fulcrum (like "Photo 1", "Photo 10")
      (item.name?.match(/^Photo \d+$/) && (
        (itemData as any).dataSourceType === 'fulcrum' ||
        currentEndpoint?.dataSourceType === 'fulcrum'
      ))
    );
    
    if (isFulcrumPhoto) {
      console.log('[DataGrid] Preventing selection of Fulcrum photo - these are attachments, not standalone entities');
      // Show user-friendly message
      enqueueSnackbar('This item does not have its own properties', { 
        variant: 'info' 
      });
      return;
    }
    
    onSelect(item);
  }, [onSelect, currentEndpoint?.tableName, enqueueSnackbar]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // Only calculate for cards view
      if (viewMode === 'cards' && entries[0]) {
        const containerWidth = entries[0].contentRect.width;
        // Calculate items per row based on minimum item width
        const minItemWidth = compact ? UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_COMPACT : UI_CONSTANTS.LAYOUT.MIN_ITEM_WIDTH_FULL; // Smaller minimum width in compact mode
        const newItemsPerRow = Math.floor(containerWidth / minItemWidth);
        const calculatedItemsPerRow = Math.max(1, newItemsPerRow);
        setItemsPerRow(calculatedItemsPerRow);
        console.log(`[DataGrid] ResizeObserver updated itemsPerRow: ${calculatedItemsPerRow} (width: ${containerWidth})`);
      }
    });

    if (parentRef.current && viewMode === 'cards') {
      observer.observe(parentRef.current);
    }

    return () => observer.disconnect();
  }, [compact, viewMode]);

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(filteredItems.length / itemsPerRow),
    getScrollElement: () => parentRef.current,
    estimateSize: () => (compact ? UI_CONSTANTS.LAYOUT.CARD_HEIGHT_COMPACT + 10 : UI_CONSTANTS.LAYOUT.CARD_HEIGHT_FULL),
    overscan: 3,
  });

  // Infinite scroll detection
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement || !onLoadMore || !hasMoreData || isLoadingMore) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const threshold = UI_CONSTANTS.LAYOUT.SCROLL_THRESHOLD; // Trigger load more when threshold reached
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        console.log('[DataGrid] Scroll threshold reached - triggering load more');
        onLoadMore();
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [onLoadMore, hasMoreData, isLoadingMore]);

  return (
    <Box sx={{ width: "100%", p: 2, ...glassStyles }}>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {(!showRelatedItems || ENABLE_SEARCH_IN_RELATED_ITEMS) && (
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search items..."
            showServerSearch={!!onServerSearch}
            localResultsCount={filteredData.data.length}
            totalResultsCount={data.length}
            serverSearchLoading={serverSearchLoading}
            onServerSearch={onServerSearch ? async (searchTerm: string) => {
              await onServerSearch(searchTerm);
              return []; // SearchBar expects SearchResult[], but we're handling results differently
            } : undefined}
            actions={{
              onRefresh,
              refreshDisabled,
              onClear: () => setSearchTerm(''),
            }}
          />
        )}
        
        <Box sx={{ ml: 2, display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
          {/* View Mode Controls - Left side, bigger and enhanced */}
          {onViewModeChange && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Switch to Card View - Display items as visual cards with thumbnails">
                <IconButton
                  onClick={() => onViewModeChange('cards')}
                  color={viewMode === 'cards' ? 'primary' : 'default'}
                  size="large" // Increased to large
                  sx={{
                    width: 48, // Bigger size
                    height: 48,
                    borderRadius: 2,
                    border: viewMode === 'cards' ? '2px solid' : '1px solid transparent',
                    borderColor: viewMode === 'cards' ? 'primary.main' : 'transparent',
                    backgroundColor: viewMode === 'cards' ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: viewMode === 'cards' ? 'primary.main' : 'action.hover',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <ViewModuleIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Switch to Table View - Display items in a detailed table format">
                <IconButton
                  onClick={() => onViewModeChange('table')}
                  color={viewMode === 'table' ? 'primary' : 'default'}
                  size="large" // Increased to large
                  sx={{
                    width: 48, // Bigger size
                    height: 48,
                    borderRadius: 2,
                    border: viewMode === 'table' ? '2px solid' : '1px solid transparent',
                    borderColor: viewMode === 'table' ? 'primary.main' : 'transparent',
                    backgroundColor: viewMode === 'table' ? 'primary.light' : 'transparent',
                    '&:hover': {
                      backgroundColor: viewMode === 'table' ? 'primary.main' : 'action.hover',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <ViewListIcon fontSize="large" />
                </IconButton>
              </Tooltip>
              {viewMode === 'table' && (
                <Tooltip title={showAllColumns ? "Show fewer columns" : "Show all columns"}>
                  <IconButton
                    onClick={() => setShowAllColumns(!showAllColumns)}
                    color={showAllColumns ? 'primary' : 'default'}
                    size="medium" // Changed from small to medium
                    sx={{
                      width: 44, // Bigger button size
                      height: 44,
                    }}
                  >
                    {showAllColumns ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          
          {/* Status Text - After view controls */}
          {onStatusText && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.85rem',
                mx: 2, // Margin on both sides
                flexGrow: 1, // Take up available space
                textAlign: 'center'
              }}
            >
              {onStatusText()}
            </Typography>
          )}
          
          {/* EndpointFilter - Right of status text, only in related items mode */}
          {showRelatedItems && allAvailableCategories.length > 1 && (
            <Box sx={{ mr: 1 }}>
              <EndpointFilter
                availableCategories={allAvailableCategories}
                selectedCategory={selectedCategoryFilter}
                onCategoryChange={setSelectedCategoryFilter}
                apiEndpoints={config.apiEndpoints}
                compact={true}
              />
            </Box>
          )}
          
          {/* Related Items Toggle - Far right */}
          {onToggleRelatedItems && relatedItemsData.length > 0 && (
            <>
              <CMButton
                onClick={onToggleRelatedItems}
                variant={showRelatedItems ? "outlined" : "contained"}
                color={showRelatedItems ? "secondary" : "primary"}
                size="small"
                sx={{ 
                  fontSize: '0.8rem', // Match EndpointFilter font size
                  fontWeight: 500,
                  height: '32px', // Match EndpointFilter height
                  minWidth: '140px', // Wider button
                  px: 3, // More padding for wider appearance
                  py: 0,
                  borderRadius: 2, // Consistent border radius (8px)
                  boxShadow: UI_CONSTANTS.SHADOWS.DEFAULT, // Same shadow as CMChip
                  textTransform: 'none', // Preserve case
                  ml: 'auto', // Push to far right
                  
                  // Ensure text is properly contained within button
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  
                  '&:hover': {
                    opacity: 0.9,
                    boxShadow: UI_CONSTANTS.SHADOWS.HOVER, // Same hover shadow as CMChip
                  },
                  
                  // Ensure the colored background covers the full button area
                  '& .MuiButton-root': {
                    height: '100%',
                    width: '100%'
                  }
                }}
              >
                {showRelatedItems ? 
                  `BACK TO ${currentEndpoint?.name?.toUpperCase() || 'ITEMS'}` : 
                  'SHOW RELATED'
                }
              </CMButton>
              <Box sx={{ width: UI_CONSTANTS.SPACING.RIGHT_GAP }} /> {/* Spacer gap */}
            </>
          )}
        </Box>
      </Box>
      {viewMode === 'table' ? (
        // Table View
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: "calc(100vh - 250px)",
            overflow: 'auto', // Enable both horizontal and vertical scrolling
            width: '100%',
            '&::-webkit-scrollbar': {
              height: 8,
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.action.hover,
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.main,
              borderRadius: 4,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            },
            ...glassStyles,
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.2)'
              : 'rgba(255, 255, 255, 0.2)',
          }}
        >
          <Table 
            stickyHeader 
            sx={{ 
              minWidth: 800, // Ensure table is wide enough to trigger horizontal scroll
              tableLayout: 'auto' // Allow columns to size naturally
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '80px', minWidth: '80px' }}>Thumbnail</TableCell>
                {/* Add Source column for related items */}
                {showRelatedItems && (
                  <TableCell sx={{ fontWeight: 'bold', width: '120px', minWidth: '120px' }}>Source</TableCell>
                )}
                    {tableColumns.map((column) => (
                      <TableCell 
                        key={column} 
                        sx={{ 
                          fontWeight: 'bold',
                          minWidth: column === 'name' ? '150px' : column === 'description' ? '300px' : '150px',
                          width: column === 'name' ? '150px' : column === 'description' ? 'auto' : '150px',
                          maxWidth: column === 'name' ? '200px' : undefined, // Allow some flexibility but keep proportional
                          whiteSpace: column === 'name' ? 'normal' : column === 'description' ? 'normal' : 'nowrap'
                        }}
                      >
                        {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                      </TableCell>
                    ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.filter(item => !item.isAddNewItem).map((item) => {
                const imageUrl = getSafeThumbnailUrl(item, currentEndpoint);
                return (
                  <TableRow 
                    key={item.id} 
                    hover 
                    onClick={() => handleSelect(item)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: showRelatedItems && (item as EnhancedRelatedItem).properties?.typeColor ? 
                        `${(item as EnhancedRelatedItem).properties!.typeColor}15` : 'transparent', // Very subtle type-based background
                      '&:hover': {
                        backgroundColor: showRelatedItems && (item as EnhancedRelatedItem).properties?.typeColor ? 
                          `${(item as EnhancedRelatedItem).properties!.typeColor}25` : theme.palette.action.hover,
                      }
                    }}
                  >
                    <TableCell sx={{ width: '80px', minWidth: '80px', textAlign: 'center' }}>
                      {imageUrl ? (
                        isFulcrumPhotoUrl(imageUrl) ? (
                          <FulcrumImage
                            src={imageUrl}
                            alt=""
                            thumbnail={true}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              objectFit: 'cover',
                              boxShadow: 1,
                              border: showRelatedItems && (item as EnhancedRelatedItem).properties?.typeColor ? 
                                `2px solid ${(item as EnhancedRelatedItem).properties!.typeColor}` : 'none',
                            }}
                          />
                        ) : (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt=""
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 2,
                              objectFit: 'cover',
                              boxShadow: 1,
                              border: showRelatedItems && (item as EnhancedRelatedItem).properties?.typeColor ? 
                                `2px solid ${(item as EnhancedRelatedItem).properties!.typeColor}` : 'none',
                            }}
                          />
                        )
                      ) : (
                        <ImageIcon sx={{ 
                          width: 60, 
                          height: 60, 
                          color: showRelatedItems && (item as EnhancedRelatedItem).properties?.typeColor ? 
                            (item as EnhancedRelatedItem).properties!.typeColor : theme.palette.text.disabled, 
                          opacity: 0.3 
                        }} />
                      )}
                    </TableCell>
                    
                    {/* Source column for related items */}
                    {showRelatedItems && (
                      <TableCell sx={{ width: '120px', minWidth: '120px', textAlign: 'left', pl: 2 }}>
                        {/* Simple text with color indicator */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Small color dot */}
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: (item as EnhancedRelatedItem).properties?.endpointColor || '#45b7d1',
                              flexShrink: 0,
                            }}
                          />
                          {/* Endpoint name as text */}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 'medium',
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {(() => {
                              const matchingEndpoint = config.apiEndpoints.find(ep => 
                                ep.returnedRelationshipsKey === (item as EnhancedRelatedItem).properties?.relatedCategory ||
                                ep.tableName === (item as EnhancedRelatedItem).properties?.relatedCategory
                              );
                              return matchingEndpoint?.name || (item as EnhancedRelatedItem).properties?.relatedCategory?.toUpperCase() || 'Unknown';
                            })()}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {tableColumns.map((column) => (
                      <TableCell 
                        key={column}
                        sx={{
                          minWidth: column === 'name' ? '150px' : column === 'description' ? '300px' : '150px',
                          width: column === 'name' ? '150px' : column === 'description' ? 'auto' : '150px',
                          maxWidth: column === 'name' ? '200px' : column === 'description' ? '500px' : undefined,
                          verticalAlign: 'top',
                          py: 1.5,
                          whiteSpace: column === 'name' ? 'normal' : column === 'description' ? 'normal' : 'nowrap',
                          wordBreak: column === 'name' ? 'break-word' : 'normal', // Allow name to break long words
                        }}
                      >
                        {column === 'name' 
                          ? getDataItemName(item, currentEndpoint || undefined)
                          : column === 'description'
                          ? getDataItemDescription(item, currentEndpoint || undefined)
                          : typeof (item as any)[column] === 'object'
                          ? JSON.stringify((item as any)[column])
                          : String((item as any)[column] || '')}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Cards View with endpoint grouping for related items
        <Box
          ref={parentRef}
          sx={{
            height: "calc(100vh - 200px)",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            position: "relative",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            // Hide scrollbar while keeping scroll functionality
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Edge
            },
          }}
        >
          {showRelatedItems && Object.keys(groupedByEndpoint).length > 0 ? (
            // Grouped display for related items
            <Box sx={{ p: 2 }}>
              
              {Object.entries(groupedByEndpoint)
                .filter(([category, items]) => {
                  // Apply category filter
                  if (selectedCategoryFilter !== 'all' && category !== selectedCategoryFilter) {
                    return false;
                  }
                  return true;
                })
                .map(([category, items]) => {
                  // Find endpoint info for this category
                  const matchingEndpoint = config.apiEndpoints.find(ep => 
                    ep.returnedRelationshipsKey === category ||
                    ep.tableName === category ||
                    ep.name.toLowerCase() === category.toLowerCase()
                  );
                  
                  const displayName = matchingEndpoint?.name || category.toUpperCase();
                  const endpointColor = matchingEndpoint ? getEntityColor(matchingEndpoint.tableName as any) : getEntityColor('related');
                  
                  // Use all items since we're not filtering by directness anymore
                  const filteredGroupItems = items;
                  
                  if (filteredGroupItems.length === 0) return null;
                  
                  return (
                    <Box key={category} sx={{ mb: 1.5 }}> {/* Reduced margin */}
                      {/* Compact Row Layout with Left Column Header */}
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'flex-start',
                        }}
                      >
                        {/* Left Column: Vertical Endpoint Title */}
                        <Box
                          sx={{
                            minWidth: '40px',
                            maxWidth: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 2,
                            backgroundColor: `${endpointColor}30`,
                            borderRadius: 1,
                            borderRight: `3px solid ${endpointColor}`,
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            minHeight: compact ? '140px' : '180px',
                          }}
                        >
                          {/* Vertical endpoint name */}
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              color: endpointColor, 
                              fontWeight: 'bold',
                              fontSize: '0.8rem',
                              writingMode: 'vertical-rl',
                              textOrientation: 'mixed',
                              transform: 'rotate(180deg)', // Rotate so text reads bottom to top
                              whiteSpace: 'nowrap',
                              letterSpacing: '1px'
                            }}
                          >
                            {displayName.toUpperCase()}
                          </Typography>
                        </Box>
                        
                        {/* Right Column: Items in Horizontal Scroll */}
                        <Box sx={{ 
                          flex: 1, 
                          overflow: 'hidden',
                          minHeight: compact ? '140px' : '180px', // Reduced height
                        }}>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1,
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              pb: 1,
                              '&::-webkit-scrollbar': {
                                height: 6,
                              },
                              '&::-webkit-scrollbar-track': {
                                backgroundColor: 'rgba(255,255,255,0.1)',
                                borderRadius: 3,
                              },
                              '&::-webkit-scrollbar-thumb': {
                                backgroundColor: endpointColor,
                                borderRadius: 3,
                              },
                            }}
                          >
                            {filteredGroupItems.map((item) => (
                              <Box 
                                key={item.id}
                                sx={{
                                  minWidth: compact ? '120px' : '160px',
                                  width: compact ? '120px' : '160px',
                                  height: compact ? '120px' : '160px',
                                  flexShrink: 0,
                                }}
                              >
                                <DataItemCard
                                  compact={true} // Always compact in grouped view
                                  item={item}
                                  imageUrl={getSafeThumbnailUrl(item, currentEndpoint)}
                                  onSelect={handleSelect}
                                  endpoint={currentEndpoint}
                                  borderColor={endpointColor} // Strong border color
                                />
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          ) : (
            // Original virtualized display for endpoint items
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: "100%",
                position: "relative",
                minHeight: "100%",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const startIndex = virtualRow.index * itemsPerRow;
                const rowItems = filteredItems.slice(startIndex, startIndex + itemsPerRow);
                
                return (
                  <Grid
                    container
                    spacing={compact ? 1 : 50}
                    sx={{
                      position: "absolute",
                      top: `${virtualRow.start}px`,
                      left: 0,
                      width: "100%",
                      "& .MuiGrid-item": {
                        width: `${100 / itemsPerRow}%`,
                        height: compact ? 120 : 400,
                        aspectRatio: compact ? undefined : "1/1",
                      },
                    }}
                    key={virtualRow.index}
                  >
                    {rowItems.map((item) => {
                      const imageUrl = getSafeThumbnailUrl(item, currentEndpoint);
                      
                      return (
                        <Grid 
                          item 
                          key={item.id}
                          sx={{
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center'
                          }}
                        >
                          {(item as any).isAddNewItem ? (
                            <AddNewCard
                              compact={compact}
                              onAdd={() => onAdd?.()}
                              entityName={currentEndpoint?.name || 'Item'}
                            />
                          ) : (
                            <DataItemCard
                              compact={compact}
                              item={item}
                              imageUrl={getSafeThumbnailUrl(item, currentEndpoint)}
                              onSelect={handleSelect}
                              endpoint={currentEndpoint}
                              borderColor={showRelatedItems ? (item as EnhancedRelatedItem).properties?.endpointColor : undefined}
                            />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                );
              })}
            </div>
          )}
          
          {/* Loading indicator for infinite scroll */}
          {isLoadingMore && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 3,
                gap: 2,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading more items...
              </Typography>
            </Box>
          )}
          
          
          {/* End of data indicator - only show when not in related mode */}
          {!showRelatedItems && !hasMoreData && filteredItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                All items loaded
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.data === nextProps.data &&
    prevProps.compact === nextProps.compact &&
    prevProps.currentEndpoint?.tableName === nextProps.currentEndpoint?.tableName &&
    prevProps.onSelect === nextProps.onSelect &&
    prevProps.searchTerm === nextProps.searchTerm &&
    prevProps.hasMoreData === nextProps.hasMoreData &&
    prevProps.isLoadingMore === nextProps.isLoadingMore &&
    prevProps.onLoadMore === nextProps.onLoadMore &&
    prevProps.onServerSearch === nextProps.onServerSearch &&
    prevProps.serverSearchLoading === nextProps.serverSearchLoading &&
    prevProps.onRefresh === nextProps.onRefresh &&
    prevProps.refreshDisabled === nextProps.refreshDisabled &&
    prevProps.onAdd === nextProps.onAdd &&
    prevProps.showAddNew === nextProps.showAddNew &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.showRelatedItems === nextProps.showRelatedItems &&
    prevProps.relatedItemsData === nextProps.relatedItemsData &&
    prevProps.selectedEntityName === nextProps.selectedEntityName
  );
});

export default DataGrid;
